import { useEffect } from "react";

const SITE_ORIGIN = "https://luviarx.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;
const SITE_NAME = "LuviaRx";

/**
 * Client-side SEO manager. Updates existing <title>, <meta>, <link> tags in
 * document.head in-place rather than appending duplicates (which is what
 * react-helmet-async does when static defaults exist in index.html).
 *
 * Static defaults live in public/index.html so JS-less link crawlers
 * (iMessage, WhatsApp, Instagram) see a valid preview. This component
 * overrides them with per-route values once the page has loaded.
 */
export default function SEO({
  title,
  description = "US-based supplier of premium research peptides and compounds. Third-party tested, batch-traceable, and shipped from Boca Raton, FL.",
  path = "",
  image,
  type = "website",
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title
    ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} | Research Compounds`;
  const url = `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`.replace(/\/$/, "") || SITE_ORIGIN;
  const ogImage = image
    ? (image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`)
    : DEFAULT_OG_IMAGE;

  useEffect(() => {
    // <title>
    if (document.title !== fullTitle) document.title = fullTitle;

    // Upsert a meta/link tag identified by selector, setting attr/value.
    const upsert = (selector, tagName, attrs, value, valueAttr = "content") => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(tagName);
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute(valueAttr, value);
    };

    upsert('meta[name="description"]', "meta", { name: "description" }, description);
    upsert('meta[name="title"]', "meta", { name: "title" }, fullTitle);
    upsert('link[rel="canonical"]', "link", { rel: "canonical" }, url, "href");

    // Open Graph
    upsert('meta[property="og:type"]', "meta", { property: "og:type" }, type);
    upsert('meta[property="og:site_name"]', "meta", { property: "og:site_name" }, SITE_NAME);
    upsert('meta[property="og:url"]', "meta", { property: "og:url" }, url);
    upsert('meta[property="og:title"]', "meta", { property: "og:title" }, fullTitle);
    upsert('meta[property="og:description"]', "meta", { property: "og:description" }, description);
    upsert('meta[property="og:image"]', "meta", { property: "og:image" }, ogImage);
    upsert('meta[property="og:image:secure_url"]', "meta", { property: "og:image:secure_url" }, ogImage);

    // Twitter
    upsert('meta[name="twitter:card"]', "meta", { name: "twitter:card" }, "summary_large_image");
    upsert('meta[name="twitter:url"]', "meta", { name: "twitter:url" }, url);
    upsert('meta[name="twitter:title"]', "meta", { name: "twitter:title" }, fullTitle);
    upsert('meta[name="twitter:description"]', "meta", { name: "twitter:description" }, description);
    upsert('meta[name="twitter:image"]', "meta", { name: "twitter:image" }, ogImage);

    // Robots (noindex)
    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.setAttribute("content", "index, follow");
    }

    // JSON-LD structured data. We manage a single <script id="seo-jsonld">
    // so route changes fully replace prior schema rather than stacking.
    const JSONLD_ID = "seo-jsonld";
    let scriptEl = document.head.querySelector(`script#${JSONLD_ID}`);
    if (jsonLd) {
      const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      const serialized = JSON.stringify(payload.length === 1 ? payload[0] : payload);
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.setAttribute("type", "application/ld+json");
        scriptEl.setAttribute("id", JSONLD_ID);
        document.head.appendChild(scriptEl);
      }
      if (scriptEl.textContent !== serialized) scriptEl.textContent = serialized;
    } else if (scriptEl) {
      scriptEl.remove();
    }
  }, [fullTitle, description, url, ogImage, type, noindex, jsonLd]);

  return null;
}
